import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Landmark, Wallet, Loader2, CheckCircle2, Tag, Share2, Gift as GiftIcon, Zap } from "lucide-react";
import { useLanguage } from "@/i18n";
import RangeCalendar from "./RangeCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ci-booking-draft";
const empty = { name: "", email: "", phone: "", check_in: "", check_out: "", guests: 2, message: "" };
const readInitial = () => {
  try {
    const draft = { ...empty, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
    if (draft.check_in && draft.check_in < new Date().toISOString().split("T")[0]) Object.assign(draft, { check_in: "", check_out: "" });
    const p = new URLSearchParams(window.location.search);
    const a = p.get("arrivo") || p.get("from");
    const b = p.get("partenza") || p.get("to");
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if (a && b && iso.test(a) && iso.test(b) && b > a) Object.assign(draft, { check_in: a, check_out: b });
    return draft;
  } catch {
    return empty;
  }
};

const readInitialGift = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return (p.get("gift") || "").toUpperCase();
  } catch { return ""; }
};

const BookingForm = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(readInitial);
  const [method, setMethod] = useState("card");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bonifico, setBonifico] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [code, setCode] = useState("");
  const [giftCode, setGiftCode] = useState(readInitialGift);

  useEffect(() => {
    axios.get(`${API}/availability`).then(({ data }) => setBlocked(data.blocked || [])).catch(() => {});
  }, [bonifico]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
  }, [form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fetchQuote = useCallback(async () => {
    if (!form.check_in || !form.check_out || form.check_out <= form.check_in) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    try {
      const { data } = await axios.get(`${API}/quote`, {
        params: {
          check_in: form.check_in, check_out: form.check_out,
          code: code.trim().toUpperCase(),
          gift_code: giftCode.trim().toUpperCase(),
        }
      });
      setQuote(data);
    } catch {
      setQuote(null);
    } finally {
      setQuoting(false);
    }
  }, [form.check_in, form.check_out, code, giftCode]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.check_in || !form.check_out) {
      toast.error(t.booking.fillAll);
      return;
    }
    if (quote && !quote.available) {
      toast.error(t.booking.unavailable);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/checkout`, {
        ...form,
        guests: Number(form.guests),
        lang,
        payment_method: method,
        origin_url: window.location.origin,
        discount_code: code.trim().toUpperCase(),
        gift_card_code: giftCode.trim().toUpperCase(),
      });
      if (data.payment_method === "bonifico") {
        setBonifico(data.bonifico);
        localStorage.removeItem(STORAGE_KEY);
        toast.success(t.booking.bonificoTitle);
      } else if (data.payment_method === "gift_card_full") {
        localStorage.removeItem(STORAGE_KEY);
        toast.success(t.booking.giftFullCovered);
        window.location.href = data.success_url;
      } else if (data.checkout_url) {
        localStorage.removeItem(STORAGE_KEY);
        toast.success(t.booking.redirecting);
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      if (err.response?.status === 409) toast.error(t.booking.unavailable);
      else toast.error(t.booking.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-5 py-3.5 rounded-xl bg-white border border-[#0D253A]/15 text-sm text-[#1A2129] placeholder:text-[#5C6773]/60 transition-colors duration-300";

  const methods = [
    { key: "card", label: t.booking.methods.card, icon: CreditCard },
    { key: "paypal", label: t.booking.methods.paypal, icon: Wallet },
    { key: "bonifico", label: t.booking.methods.bonifico, icon: Landmark },
  ];

  return (
    <section id="prenota" data-testid="booking-section" className="py-20 lg:py-32 px-6 sm:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] mb-4">{t.booking.eyebrow}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0D253A]">{t.booking.title}</h2>
          <p className="mt-5 text-base sm:text-lg text-[#333D47] leading-relaxed">{t.booking.text}</p>
          <div data-testid="long-stay-hint" className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C86D51]/10 border border-[#C86D51]/25 text-xs font-semibold text-[#C86D51] uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C86D51]" />
            {t.booking.longStayHint}
          </div>
          <div className="mt-10 rounded-2xl overflow-hidden img-frame shadow-[0_25px_55px_-25px_rgba(13,37,58,0.4)]">
            <img
              src="https://images.pexels.com/photos/3649209/pexels-photo-3649209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Casa vacanze vicino al mare"
              className="w-full h-72 object-cover"
            />
          </div>
        </motion.div>

        {bonifico ? (
          <motion.div
            data-testid="booking-bonifico-panel"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-white rounded-3xl border border-[#0D253A]/10 p-8 sm:p-10 shadow-[0_25px_60px_-30px_rgba(13,37,58,0.3)]"
          >
            <CheckCircle2 className="w-12 h-12 text-green-600" />
            <h3 className="font-display text-2xl text-[#0D253A] mt-5">{t.booking.bonificoTitle}</h3>
            <p className="mt-3 text-sm text-[#5C6773] leading-relaxed">{t.booking.bonificoDesc}</p>
            <div className="mt-6 rounded-2xl bg-[#F8F5F0] border border-[#E3DED6] p-6 flex flex-col gap-2 text-sm">
              {[
                [t.booking.holder, bonifico.holder],
                ["IBAN", bonifico.iban],
                ["BIC/SWIFT", bonifico.bic],
                [t.booking.amount, `€ ${bonifico.amount}`],
                [t.booking.causale, bonifico.causale],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[#E3DED6] last:border-0 pb-2 last:pb-0">
                  <span className="text-[#5C6773]">{k}</span>
                  <span className="font-semibold text-[#0D253A] text-right break-all">{v}</span>
                </div>
              ))}
            </div>
            <button
              data-testid="booking-bonifico-new"
              onClick={() => { setBonifico(null); setForm(empty); setQuote(null); }}
              className="mt-8 px-6 py-3 bg-[#0D253A] text-[#F8F5F0] text-sm font-semibold rounded-full hover:bg-[#C86D51] transition-colors"
            >
              OK
            </button>
          </motion.div>
        ) : (
          <motion.form
            data-testid="booking-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 bg-white rounded-3xl border border-[#0D253A]/10 p-8 sm:p-10 shadow-[0_25px_60px_-30px_rgba(13,37,58,0.3)] flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.name}</label>
                <input data-testid="booking-name-input" type="text" value={form.name} onChange={set("name")} className={inputCls} placeholder="Maria Rossi" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.email}</label>
                <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="maria@email.it" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.phone}</label>
                <input data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+39 333 1234567" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.guests}</label>
                <select data-testid="booking-guests-select" value={form.guests} onChange={set("guests")} className={inputCls}>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n} {t.booking.guestsSuffix}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.datesLabel}</label>
              <RangeCalendar
                blocked={blocked}
                checkIn={form.check_in}
                checkOut={form.check_out}
                onChange={(r) => setForm((f) => ({ ...f, ...r }))}
                testidPrefix="booking"
              />
            </div>

            {/* Discount code + Gift card + WhatsApp share */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Tag className="w-4 h-4 text-[#5C6773] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="booking-code-input"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t.booking.codePlaceholder}
                  className={`${inputCls} pl-10 uppercase tracking-wider`}
                  aria-label={t.booking.codeLabel}
                />
                {quote && code && quote.code_status === "invalid" && (
                  <p data-testid="booking-code-invalid" className="mt-1.5 text-xs text-[#C86D51]">{t.booking.codeInvalid}</p>
                )}
              </div>
              <div className="relative">
                <GiftIcon className="w-4 h-4 text-[#5C6773] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="booking-gift-input"
                  type="text"
                  value={giftCode}
                  onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                  placeholder={t.booking.giftPlaceholder}
                  className={`${inputCls} pl-10 uppercase tracking-wider`}
                  aria-label={t.booking.giftLabel}
                />
                {quote && giftCode && quote.gift_status === "invalid" && (
                  <p data-testid="booking-gift-invalid" className="mt-1.5 text-xs text-[#C86D51]">{t.booking.giftInvalid}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="booking-share-whatsapp"
                onClick={() => {
                  const url = new URL(window.location.origin);
                  if (form.check_in && form.check_out) {
                    url.searchParams.set("arrivo", form.check_in);
                    url.searchParams.set("partenza", form.check_out);
                  }
                  const link = url.toString();
                  const msg = form.check_in && form.check_out && quote?.available
                    ? t.booking.shareWithDates
                        .replace("{from}", form.check_in)
                        .replace("{to}", form.check_out)
                        .replace("{nights}", quote.nights)
                        .replace("{total}", quote.total.toFixed(2))
                        .replace("{url}", link)
                    : t.booking.shareGeneric.replace("{url}", link);
                  const w = /iPhone|iPad|Android/i.test(navigator.userAgent) ? "whatsapp://" : "https://web.whatsapp.com/";
                  window.open(`${w}send?text=${encodeURIComponent(msg)}`, "_blank");
                }}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 text-[#0D253A] text-xs font-semibold hover:bg-[#25D366]/20 transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" /> {t.booking.shareBtn}
              </button>
            </div>

            {/* Price summary */}
            <div data-testid="booking-quote" className="rounded-2xl bg-[#F8F5F0] border border-[#E3DED6] p-5">
              {quoting ? (
                <p className="text-sm text-[#5C6773] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> …</p>
              ) : quote && quote.available ? (
                <>
                  <div className="flex items-end justify-between">
                    <span className="text-xs uppercase tracking-wider text-[#5C6773]">{t.booking.totalLabel} · {quote.nights} {t.booking.nightsLabel}</span>
                    <span data-testid="booking-total" className="font-display text-3xl text-[#0D253A]">€ {quote.total.toFixed(2)}</span>
                  </div>
                  {Array.isArray(quote.composition) && quote.composition.length > 0 && (
                    <div data-testid="booking-composition" className="mt-2 flex flex-wrap gap-1.5">
                      {quote.composition.map((c, i) => (
                        <span key={i} className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                          c.category === "event" ? "bg-[#C86D51]/15 text-[#C86D51]" :
                          c.category === "period" ? "bg-[#1E5F74]/15 text-[#1E5F74]" :
                          c.category === "weekend" ? "bg-amber-100 text-amber-800" :
                          c.category === "high_season" ? "bg-[#0D253A]/10 text-[#0D253A]" :
                          "bg-[#5C6773]/10 text-[#5C6773]"
                        }`}>
                          {(t.booking.catLabel[c.category] || c.category)} × {c.count} · € {Number(c.rate_min).toFixed(0)}{c.rate_min !== c.rate_max ? `–${Number(c.rate_max).toFixed(0)}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {quote.discount_amount > 0 && (
                    <p className="mt-2 text-xs text-[#C86D51] font-semibold">{t.booking.discountLabel}: −€ {quote.discount_amount.toFixed(2)}</p>
                  )}
                  {quote.last_minute_amount > 0 && (
                    <p data-testid="booking-lm-applied" className="mt-1 text-xs text-[#C86D51] font-semibold flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {t.booking.lastMinuteLabel} (−{quote.last_minute_pct.toFixed(0)}%): −€ {quote.last_minute_amount.toFixed(2)}
                    </p>
                  )}
                  {quote.code_amount > 0 && (
                    <p data-testid="booking-code-applied" className="mt-1 text-xs text-[#0D253A] font-semibold">{t.booking.codeApplied}: {quote.code_label} · −€ {quote.code_amount.toFixed(2)}</p>
                  )}
                  {quote.gift_used > 0 && (
                    <p data-testid="booking-gift-applied" className="mt-1 text-xs text-[#0D253A] font-semibold">
                      {t.booking.giftApplied}: {quote.gift_code} · −€ {quote.gift_used.toFixed(2)}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-[#5C6773] leading-relaxed">{t.booking.taxNote} {t.booking.depositNote}</p>
                </>
              ) : quote && !quote.available ? (
                <p data-testid="booking-unavailable" className="text-sm font-semibold text-[#C86D51]">{t.booking.unavailable}</p>
              ) : (
                <p className="text-sm text-[#5C6773]">{t.booking.selectDates}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{t.booking.message}</label>
              <textarea
                data-testid="booking-message-input"
                rows={3}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
                placeholder={t.booking.messagePh}
              />
            </div>

            {/* Payment method */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-3">{t.booking.payTitle}</label>
              <div className="grid grid-cols-3 gap-3">
                {methods.map((m) => {
                  const Icon = m.icon;
                  const active = method === m.key;
                  return (
                    <button
                      type="button"
                      key={m.key}
                      data-testid={`booking-method-${m.key}`}
                      onClick={() => setMethod(m.key)}
                      className={`flex flex-col items-center gap-2 px-3 py-4 rounded-xl border text-xs font-semibold transition-colors ${
                        active ? "border-[#C86D51] bg-[#C86D51]/10 text-[#0D253A]" : "border-[#0D253A]/15 text-[#5C6773] hover:border-[#C86D51]/50"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${active ? "text-[#C86D51]" : ""}`} />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={submitting || (quote && !quote.available)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors duration-300"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {submitting ? t.booking.redirecting : t.booking.payNow}
            </button>
            <p data-testid="booking-consent-note" className="text-xs text-[#5C6773] leading-relaxed">
              {t.legal.consent.split(/(\{privacy\}|\{terms\})/).map((part, i) =>
                part === "{privacy}" ? (
                  <Link key={i} to="/privacy" className="underline hover:text-[#C86D51]">{t.legal.privacy}</Link>
                ) : part === "{terms}" ? (
                  <Link key={i} to="/termini" className="underline hover:text-[#C86D51]">{t.legal.terms}</Link>
                ) : (
                  part
                )
              )}
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default BookingForm;
